import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import {
  GlobalTagFormSchema,
  GlobalTagsModel,
  GlobalUpdateTagMutationParamsSchema,
} from '@/features/tag/models';
import { useInvalidateQuery } from '@/hooks';
import type { z } from 'zod';
import { queryKeyMap as tagQueryKeyMap } from '../utils';

export const useGlobalTags = <
  T extends object,
  U = z.infer<typeof GlobalTagsModel>,
>({
  params,
  select,
}: {
  params?: T;
  select?: (data: z.infer<typeof GlobalTagsModel>) => U;
} = {}) => {
  return useQuery({
    queryKey: tagQueryKeyMap.globalTags(params),
    queryFn: async () => {
      const res = await axiosGet('/api/tags/global/', params);
      return GlobalTagsModel.parse(res);
    },
    select,
  });
};

export const useCreateGlobalTag = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (values: z.infer<typeof GlobalTagFormSchema>) => {
      const { period, ...payload } = GlobalTagFormSchema.parse(values);
      return await axiosPost('/api/tags/global/', {
        ...payload,
        startYear: period?.[0]?.year() ?? null,
        endYear: period?.[1]?.year() ?? null,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        invalidate(tagQueryKeyMap.tags()),
        invalidate(tagQueryKeyMap.globalTags()),
      ]);
    },
  });
};

export const useUpdateGlobalTag = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof GlobalUpdateTagMutationParamsSchema>,
    ) => {
      const { id, period, ...payload } =
        GlobalUpdateTagMutationParamsSchema.parse(values);
      return await axiosPatch(`/api/tags/global/${id}/`, {
        ...payload,
        startYear: period?.[0]?.year() ?? null,
        endYear: period?.[1]?.year() ?? null,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        invalidate(tagQueryKeyMap.tags()),
        invalidate(tagQueryKeyMap.globalTags()),
      ]);
    },
  });
};

export const useDeleteGlobalTag = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (id: string) => {
      return await axiosDelete(`/api/tags/global/${id}/`);
    },
    onSuccess: async () => {
      await Promise.all([
        invalidate(tagQueryKeyMap.tags()),
        invalidate(tagQueryKeyMap.globalTags()),
      ]);
    },
  });
};
